/**
 * The console shell: one sticky command bar, one main region, one footer that states
 * which contract revision this bundle was built against (the client is generated, so
 * the footer is also a build receipt).
 */
import { Link, Outlet } from '@tanstack/react-router'
import contract from '@/api/contract.json'
import { isMockEnabled } from '@/mocks/mode'
import { OpsBudgetWidget } from './OpsBudgetWidget'

export function AppShell() {
  return (
    <div className="grain relative min-h-screen">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-forge-500 focus:px-3 focus:py-1.5 focus:text-sm focus:font-semibold focus:text-ink-950"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-30 border-b border-ink-750 bg-ink-950/88 backdrop-blur">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-x-5 gap-y-2 px-5 py-2.5">
          <Link to="/" className="flex items-center gap-2.5 rounded-md py-1">
            <img src="/mark.svg" alt="" width={22} height={22} className="rounded-[5px]" />
            <span className="text-[14px] font-semibold tracking-tight text-mist-100">AdForge</span>
            <span className="label-cap hidden sm:inline">Console</span>
          </Link>

          <nav aria-label="Sections" className="flex items-center gap-1 text-[13px]">
            <Link
              to="/"
              className="rounded-md px-2.5 py-1.5 text-mist-300 transition-colors hover:bg-ink-800 hover:text-mist-100"
              activeProps={{ className: 'bg-ink-800 text-mist-100' }}
              activeOptions={{ exact: true }}
            >
              New project
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {isMockEnabled() ? (
              <span
                data-testid="mock-banner"
                title="This build serves every API call from MSW mocks. No backend is running."
                className="inline-flex items-center gap-1.5 rounded-full border border-signal-ai/45 bg-signal-ai/10 px-2.5 py-[3px] text-[11px] font-semibold tracking-wide text-signal-ai"
              >
                <span className="pulse-dot size-1.5 rounded-full bg-signal-ai" />
                MOCK DATA
              </span>
            ) : null}
            <OpsBudgetWidget />
          </div>
        </div>
      </header>

      <main id="main" className="relative z-10 mx-auto max-w-[1440px] px-5 py-6">
        <Outlet />
      </main>

      <footer className="relative z-10 mx-auto max-w-[1440px] px-5 pb-8">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-ink-800 pt-3 text-[11.5px] text-mist-500">
          <span>
            Client generated from <code className="num text-mist-400">{contract.spec_path}</code> —{' '}
            {contract.path_count} paths, OpenAPI {contract.openapi}
          </span>
          <span className="num">sha256 {contract.spec_sha256.slice(0, 12)}</span>
          <span className="num ml-auto">API base: {(import.meta.env.VITE_API_BASE_URL as string) || 'same origin'}</span>
        </div>
      </footer>
    </div>
  )
}
